import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Email,
  Phone,
  Business,
  CalendarToday,
  Security,
  Lock,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { colors } from '../theme/theme';

export const MyProfilePage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', new_password_confirmation: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch fresh user data
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/user');
      return (res.data as any)?.data || res.data;
    },
  });

  const profile = profileData || user;

  const changePasswordMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/admin/change-password', data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      setChangePasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', new_password_confirmation: '' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to change password', severity: 'error' });
    },
  });

  const handleChangePassword = () => {
    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      setSnackbar({ open: true, message: 'New passwords do not match', severity: 'error' });
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setSnackbar({ open: true, message: 'Password must be at least 8 characters', severity: 'error' });
      return;
    }
    changePasswordMutation.mutate(passwordForm);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  const roles = profile?.roles || [];
  const permissions = profile?.permissions || [];
  const rolePermissions = roles.flatMap((r: any) => r.permissions?.map((p: any) => p.name) || []);
  const allPerms = Array.from(new Set([...rolePermissions, ...permissions.map((p: any) => p.name)]));

  return (
    <AdminLayout>
      <Box>
        <PageHeader title="My Profile" subtitle="View and manage your account"
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'My Profile' }]}
        />

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: colors.primary.main, fontSize: 40 }}>
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>
                  {profile?.first_name} {profile?.middle_name ? profile.middle_name + ' ' : ''}{profile?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {profile?.email}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                  {roles.map((r: any) => (
                    <Chip key={r.id} label={r.name} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                  ))}
                  {roles.length === 0 && <Chip label="No role assigned" size="small" variant="outlined" />}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Button fullWidth variant="outlined" startIcon={<Lock />} onClick={() => setChangePasswordOpen(true)}>
                  Change Password
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Details */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>Account Details</Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Email sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile?.email}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Phone sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile?.profile?.phone || profile?.phone || 'Not set'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Business sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">County</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile?.county?.name || 'N/A'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Security sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Account Type</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{profile?.user_type || 'N/A'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Account Created</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Last Login</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {profile?.last_login_at ? new Date(profile.last_login_at).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Permissions */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>My Permissions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {allPerms.length} permission{allPerms.length !== 1 ? 's' : ''} granted via {roles.length > 0 ? `role "${roles.map((r: any) => r.name).join(', ')}"` : 'direct assignment'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {allPerms.length > 0 ? allPerms.map((perm: string, i: number) => (
                    <Chip key={i} label={perm.replace(/_/g, ' ')} size="small" variant="outlined"
                      sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }} />
                  )) : (
                    <Typography variant="body2" color="text.secondary">No permissions assigned</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Change Password Dialog */}
        <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField fullWidth required type="password" label="Current Password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
              <TextField fullWidth required type="password" label="New Password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                helperText="Minimum 8 characters" />
              <TextField fullWidth required type="password" label="Confirm New Password"
                value={passwordForm.new_password_confirmation}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password_confirmation: e.target.value })}
                error={passwordForm.new_password_confirmation.length > 0 && passwordForm.new_password !== passwordForm.new_password_confirmation}
                helperText={passwordForm.new_password_confirmation.length > 0 && passwordForm.new_password !== passwordForm.new_password_confirmation ? 'Passwords do not match' : ''} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)} startIcon={<Cancel />}>Cancel</Button>
            <Button variant="contained" onClick={handleChangePassword}
              disabled={changePasswordMutation.isPending || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.new_password_confirmation}
              startIcon={changePasswordMutation.isPending ? <CircularProgress size={20} /> : <Save />}>
              {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};
